import { ClubCard } from '@/components/clubs/ClubCard';
import { useClubFilters } from '@/context/ClubFiltersContext';
import { useClubs } from '@/hooks/useClubs';
import { filterClubs } from '@/services/clubFilterService';
import styles from '@/pages/ClubsPage.module.css';

function clubKey(club: { clubId: string | null; app: string | null; clubName: string | null }, index: number): string {
  return `${club.clubId ?? 'id'}-${club.app ?? 'app'}-${club.clubName ?? 'name'}-${index}`;
}

export function ClubsPage() {
  const { data, error, loading } = useClubs();
  const { filters, setSearchQuery, hasActiveFilters } = useClubFilters();
  const allClubs = data?.items ?? [];
  const filteredClubs = filterClubs(allClubs, filters);
  const totalCount = allClubs.length;
  const visibleCount = filteredClubs.length;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Clubs</h1>
            <p className={styles.subtitle}>
              {loading || error || totalCount === 0
                ? 'Browse available poker clubs'
                : hasActiveFilters
                  ? `Showing ${visibleCount} of ${totalCount} clubs`
                  : `${totalCount} club${totalCount === 1 ? '' : 's'} available`}
            </p>
          </div>
          {!loading && !error && totalCount > 0 && (
            <div className={styles.searchWrapper}>
              <label htmlFor="club-search" className={styles.searchLabel}>
                Search clubs
              </label>
              <input
                id="club-search"
                type="search"
                className={styles.searchInput}
                placeholder="Search by club name or ID"
                value={filters.searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </div>
      </header>

      {loading && (
        <div className={styles.stateMessage}>
          <p>Loading clubs…</p>
        </div>
      )}

      {!loading && error && (
        <div className={styles.stateMessage}>
          <p className={styles.error}>Unable to load clubs: {error.message}</p>
        </div>
      )}

      {!loading && !error && data && data.count === 0 && (
        <div className={styles.stateMessage}>
          <p>No clubs in the directory yet. Check back after the next sync.</p>
        </div>
      )}

      {!loading && !error && data && data.count > 0 && visibleCount === 0 && (
        <div className={styles.stateMessage}>
          <p>No clubs match your search or filters.</p>
        </div>
      )}

      {!loading && !error && data && data.count > 0 && visibleCount > 0 && (
        <div className={styles.grid}>
          {filteredClubs.map((club, index) => (
            <ClubCard key={clubKey(club, index)} club={club} />
          ))}
        </div>
      )}
    </div>
  );
}
